/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useEffect, useState } from "react";
import { buildImageUrl } from "../../theme1/utils/theme1Helpers";
import { ORDER_STATUS } from "../utils/theme2Constants";
import { formatNumber } from "utils/formatNumber";
import { getDeliveryByCity } from "../../utils/storeApi";

export default function OrderDetailsPage({ order, theme, onClose, onEdit, onDelete }) {
  const [shipping, setShipping] = useState(0);

  // Logic from theme1 (ideas)
  const customerName =
    order.customerName ||
    JSON.parse(localStorage.getItem("storeCustomer") || "{}")?.name ||
    "-";

  const paymentStatusText = Number(order.isPaid) === 1 ? "مدفوع" : "غير مدفوع";

  const validDetails = (order.details || []).map(d => ({
    itemId: d.itemId || d.ItemId,
    variantId: d.variantId || d.VariantId,
    itemName: d.itemName || d.ItemName,
    variantName: d.variantName || d.VariantName,
    colorName: d.colorName || d.ColorName || d.color,
    size: d.size || d.Size,
    quantity: Number(d.quantity || d.Quantity || 0),
    price: Number(d.price || d.Price || 0),
    lineTotal: Number(d.lineTotal || d.LineTotal || 0),
    imageUrl: d.imageUrl || d.ImageUrl
  })).filter(d => d.quantity > 0);
  const subTotal = validDetails.reduce((sum, item) => sum + (Number(item.lineTotal) || (Number(item.price) * Number(item.quantity)) || 0), 0);
  const discount = Number(order.discountAmount || 0);
  const finalTotal = Math.max(subTotal - discount + Number(shipping || 0), 0);

  useEffect(() => {
    let alive = true;
    if (!order) return;
    const tenantId = Number(order.tenantID || order.tenantId || 0);
    const cityId = Number(order.cityId || 0);
    if (!tenantId || !cityId) return;
    getDeliveryByCity(tenantId, cityId).then((res) => {
      if (!alive) return;
      setShipping(Number(res?.deliveryCompanyFees || res?.deliveryFees || res?.fee || 0));
    }).catch(() => { if (alive) setShipping(0); });
    return () => { alive = false; };
  }, [order]);

  if (!order) return null;
  const isNewOrder = Number(order.statusId) === ORDER_STATUS.PENDING;

  return (
    <div className={`fixed inset-0 z-[26000] bg-gradient-to-br ${theme.bgGradient} overflow-y-auto p-0`}>
      <div className="w-full min-h-screen flex flex-col bg-transparent shadow-2xl relative">

        {/* Header */}
        <div className="flex justify-between items-center px-8 py-6 border-b border-white/10 bg-white/5 backdrop-blur-md sticky top-0 z-20">
          <div>
            <h3 className="text-xl font-bold text-white">
              تفاصيل الطلب #{order.orderNo || order.orderId?.slice(0, 6)}
            </h3>
            <p className="text-white/40 text-xs mt-1">عرض حالة الطلب والمنتجات والأسعار</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors text-white text-2xl">
            ×
          </button>
        </div>

        {/* Info Grid - Premium Style */}
        <div className="bg-white/5 mx-6 mt-6 rounded-2xl p-5 border border-white/5 grid grid-cols-2 gap-y-3 gap-x-6 text-[13px]">
          <div className="text-white/50">العميل: <span className="text-white font-bold">{customerName}</span></div>
          <div className="text-white/50">الهاتف: <span className="text-white font-bold">{order.customerPhone1 || "-"}</span></div>
          <div className="text-white/50">المحافظة: <span className="text-white font-bold">{order.city || "-"}</span></div>
          <div className="text-white/50">العنوان: <span className="text-white font-bold">{order.address || "-"}</span></div>
          <div className="text-white/50">طريقة الدفع: <span className="text-white font-bold">{order.paymentMethodName || "-"}</span></div>
          <div className="text-white/50">حالة الدفع: <span className="text-white font-bold" style={{ color: Number(order.isPaid) === 1 ? '#4ade80' : '#f87171' }}>{paymentStatusText}</span></div>
          <div className="text-white/50">حالة الطلب: <span className="text-white font-bold px-2 py-0.5 rounded-md bg-white/10 text-[11px]">{order.status || "-"}</span></div>
          <div className="text-white/50">التاريخ: <span className="text-white font-bold">{order.orderDate || "-"}</span></div>
          {order.coupon && <div className="col-span-2 text-white/50 border-t border-white/5 pt-2 mt-1 italic text-xs">الكوبون المستخدم: <span className="text-[#4ade80] font-black">{order.coupon}</span></div>}
        </div>

        {/* Items List */}
        <div className="flex-1 px-6 py-4 space-y-4 pb-10">
          <h4 className="text-xs font-black text-white/30 uppercase tracking-widest mb-2 px-1">محتويات الشحنة</h4>
          {validDetails.length === 0 ? (
            <div className="h-40 flex flex-col items-center justify-center text-white/20 border-2 border-dashed border-white/5 rounded-3xl">
              <span>لا توجد تفاصيل لهذا الطلب</span>
            </div>
          ) : (
            <>
              {validDetails.map((item, i) => (
                <div
                  key={i}
                  className="flex flex-col sm:flex-row gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.05] transition-all group"
                >
                  <div className="w-20 h-20 bg-white rounded-xl overflow-hidden flex-shrink-0 shadow-lg p-1 mx-auto sm:mx-0">
                    <img
                      src={buildImageUrl(item.imageUrl)}
                      onError={e => e.target.src = "https://placehold.co/70x70?text=No+Image"}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                    <div>
                      <div className="font-bold text-white text-base truncate pr-1">{item.itemName || "منتج"}</div>

                      <div className="flex flex-wrap items-center gap-3 mt-1.5 h-6">
                        {item.variantName && (
                          <div className="px-2 py-0.5 bg-white/5 rounded-md border border-white/5 text-[10px] text-white/60">
                            {item.variantName}
                          </div>
                        )}

                        {item.colorName && (
                          <div className="flex items-center gap-1.5 px-2 py-0.5 bg-white/5 rounded-md border border-white/5">
                            <div className="w-2.5 h-2.5 rounded-full border border-white/20 shadow-sm" style={{ background: item.colorName }} />
                            <span className="text-[10px] text-white/60">لون</span>
                          </div>
                        )}

                        {item.size && (
                          <div className="px-2 py-0.5 bg-white/5 rounded-md border border-white/5 text-[10px] text-white/60 font-black">
                            {item.size}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mt-2">
                      <div className="text-[11px] text-white/40">
                        الكمية: <span className="text-white font-bold">{item.quantity}</span>
                      </div>
                      <div className="font-black text-lg sm:text-right" style={{ color: theme.primary }}>
                        {formatNumber(item.lineTotal || (item.price * item.quantity))} <span className="text-[10px] opacity-60 font-normal">IQD</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* مجموع الحساب - ثيم 2 بريميوم */}
              <div className="mt-8 bg-white/[0.02] rounded-3xl p-6 border border-white/5 space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/40 font-bold">المجموع الفرعي</span>
                  <span className="text-white font-black">{formatNumber(subTotal)} د.ع</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between items-center text-sm text-red-400">
                    <span className="font-bold">الخصم</span>
                    <span className="font-black">-{formatNumber(discount)} د.ع</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/40 font-bold">أجور التوصيل</span>
                  <span className="text-white font-black">
                    {Number(shipping) <= 0 ? (
                      <span className="text-green-400">مجاني ✨</span>
                    ) : (
                      `${formatNumber(shipping)} د.ع`
                    )}
                  </span>
                </div>
                <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                  <span className="text-lg font-black text-white uppercase tracking-widest">الإجمالي النهائي</span>
                  <div className="text-right">
                    <span className="text-2xl font-black" style={{ color: theme.primary }}>{formatNumber(finalTotal)}</span>
                    <span className="text-[10px] text-white/40 mr-1 font-bold">IQD</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-white/10 bg-white/5 backdrop-blur-xl flex gap-4 sticky bottom-0 z-20">
          {isNewOrder && (
            <button
              onClick={() => onEdit(order)}
              className="flex-1 py-4 rounded-2xl font-black text-sm shadow-xl transition-all active:scale-95 flex items-center justify-center hover:brightness-110"
              style={{ background: theme.primary, color: "#000" }}
            >
              تعديل الطلب
            </button>
          )}

          {(isNewOrder && Number(order.isPaid) === 0) && (
            <button
              onClick={() => onDelete(order)}
              className="flex-1 py-4 rounded-2xl font-black text-sm bg-red-500/10 border border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white transition-all active:scale-95 flex items-center justify-center"
            >
              حذف الطلب
            </button>
          )}
        </div>
      </div>

      <style jsx="true">{`
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.05); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.1); }
      `}</style>
    </div>
  );
}


